/* eslint-disable react/require-default-props */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Modal from '../../../../../components/Modal/Modal';
import { PrimaryButton, GhostButton } from '../../../../../styles/ui/Button';
import { admitOnboarding } from '../../../../../services/hr/recruitment/onboardingApi';
import {
   ONBOARDING_STATUS,
   ONBOARDING_STATUS_LABEL,
   ADMIT_REQUIRED_PRE_CADASTRO_FIELDS,
} from '../../constants';
import { readApiError, recruitmentErrorMessage } from '../../utils/apiError';

// ---------------------------------------------------------------------------
// ADMITIR — conversão candidato → funcionário. O pré-check client-side espelha
// a validação do backend (§6 do handoff 0065): status AGUARDANDO_INICIO + 5
// campos do pré-cadastro + data de início + CPF do candidato. Requisito
// faltando = Confirmar DESABILITADO (bloqueante = não-acionável, H.3.1) — o
// operador não descobre no submit. 409 = CPF ativo duplicado.
// ---------------------------------------------------------------------------

const CheckList = styled.ul`
   list-style: none;
   margin: 12px 0 0;
   padding: 0;
   display: grid;
   gap: 6px;

   li {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 0.86rem;
   }
   li .check-icon {
      width: 18px;
      text-align: center;
      font-weight: 800;
   }
   li.ok .check-icon {
      color: #1b5e20;
   }
   li.missing {
      color: #8a1f1f;
   }
`;

// [{ label, ok }] — requisitos do admit avaliados contra o estado atual.
function buildRequirements(onboarding, candidate) {
   const requirements = [
      {
         label: `Etapa "${ONBOARDING_STATUS_LABEL.AGUARDANDO_INICIO}"`,
         ok: onboarding?.status === ONBOARDING_STATUS.AGUARDANDO_INICIO,
      },
   ];
   ADMIT_REQUIRED_PRE_CADASTRO_FIELDS.forEach(({ key, label }) => {
      const value = onboarding?.[key];
      requirements.push({
         label: `${label} no pré-cadastro`,
         ok: value !== null && value !== undefined && value !== '',
      });
   });
   requirements.push({
      label: 'Data de início no pré-cadastro',
      ok: Boolean(onboarding?.start_date),
   });
   requirements.push({
      label: 'CPF do candidato',
      ok: Boolean(candidate?.cpf),
   });
   return requirements;
}

export default function AdmitModal({
   isOpen,
   onClose,
   onboarding,
   candidate,
   collectedFileCount = 0,
   onAdmitted,
   onRefresh,
}) {
   const [isSubmitting, setIsSubmitting] = useState(false);

   const requirements = buildRequirements(onboarding, candidate);
   const allOk = requirements.every((requirement) => requirement.ok);

   const handleConfirm = async () => {
      if (isSubmitting || !allOk) return;
      setIsSubmitting(true);
      try {
         const { employeeId, documentsLinked, message } = await admitOnboarding(
            onboarding.id,
         );
         toast.success(
            `${message} ${documentsLinked} documento(s) vinculado(s) à ficha.`,
         );
         onClose?.();
         onAdmitted?.(employeeId);
      } catch (err) {
         const { status } = readApiError(err);
         if (status === 409) {
            toast.error('Já existe um funcionário ativo com este CPF.');
         } else {
            toast.error(
               recruitmentErrorMessage(err, 'Erro ao admitir o candidato.'),
            );
         }
         // Estado pode estar defasado (outro operador mexeu) — ressincroniza.
         onRefresh?.();
      } finally {
         setIsSubmitting(false);
      }
   };

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title="Admitir funcionário"
         maxWidth="520px"
         footer={
            <>
               <GhostButton
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
               >
                  Cancelar
               </GhostButton>
               <PrimaryButton
                  type="button"
                  onClick={handleConfirm}
                  disabled={isSubmitting || !allOk}
               >
                  {isSubmitting ? 'Admitindo…' : 'Admitir funcionário'}
               </PrimaryButton>
            </>
         }
      >
         <p style={{ margin: 0, fontSize: '0.9rem' }}>
            Admitir <strong>{candidate?.full_name ?? 'o candidato'}</strong>{' '}
            cria o cadastro de funcionário com os dados do pré-cadastro, copia{' '}
            <strong>{collectedFileCount}</strong> documento(s) coletado(s) para
            a ficha e encerra este onboarding. A ação não pode ser desfeita.
         </p>

         <CheckList>
            {requirements.map((requirement) => (
               <li
                  key={requirement.label}
                  className={requirement.ok ? 'ok' : 'missing'}
               >
                  <span className="check-icon">
                     {requirement.ok ? '✓' : '✗'}
                  </span>
                  {requirement.label}
               </li>
            ))}
         </CheckList>

         {!allOk && (
            <p
               style={{
                  margin: '12px 0 0',
                  fontSize: '0.8rem',
                  color: '#8a1f1f',
               }}
            >
               Complete os itens pendentes (pré-cadastro / etapa do pipeline)
               para liberar a admissão.
            </p>
         )}
      </Modal>
   );
}

AdmitModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   onboarding: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      status: PropTypes.string,
      start_date: PropTypes.string,
   }),
   candidate: PropTypes.shape({
      full_name: PropTypes.string,
      cpf: PropTypes.string,
   }),
   collectedFileCount: PropTypes.number,
   onAdmitted: PropTypes.func,
   onRefresh: PropTypes.func,
};
